/**
 * O cliente Supabase do browser.
 *
 * Fonte normativa: SCRIPT G7, §7 (projeto 2.0 exclusivo), §17 (segredos) e §51
 * (Supabase Auth é a autoridade de autenticação).
 *
 * DOCUMENTAÇÃO OFICIAL CONSULTADA (§4)
 *
 *   supabase.com/docs/reference/javascript/initializing — opções de `auth`:
 *   `persistSession` (padrão true, localStorage), `autoRefreshToken` (true),
 *   `detectSessionInUrl` (true), `flowType` (padrão `implicit`).
 *   Consultado em 2026-09-08.
 *
 *   supabase.com/docs/guides/api/api-keys — sobre a chave publicável:
 *   _"Safe to expose online: web page, mobile or desktop app…"_ e
 *   _"Anyone can read it, so it only reaches what Row Level Security allows."_
 *   Consultado em 2026-09-08.
 *
 * POR QUE UMA CHAVE NO BUNDLE NÃO CONTRARIA O §17
 *
 *   O §17 proíbe `service_role`, token GIAP, senhas, PFX, private keys, AES e
 *   segredos de Cloudflare. A chave publicável não é nenhum deles: ela não
 *   concede nada por si: identifica o projeto, e **quem autoriza é a RLS**.
 *
 *   A diferença é a coisa toda. `service_role` ignora RLS por desenho; a chave
 *   publicável só chega onde a política deixa. Guardá-la num backend não
 *   aumentaria segurança nenhuma e criaria um proxy que reimplementaria
 *   autorização — que é exatamente o que o §104 proíbe.
 *
 *   O guard `frontend-sem-service-role` existe para que a distinção não dependa
 *   de alguém lembrar dela.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/**
 * O ref do projeto 2.0, escrito por extenso (§7).
 *
 * Ele fica aqui, e não só na variável de ambiente, para que uma configuração
 * apontada para outro projeto seja recusada em tempo de execução — inclusive a
 * do legado, que o §42 nomeia. Uma variável errada num deploy é silenciosa; esta
 * conferência não é.
 */
export const OPTEFISCAL_PROJECT_REF = 'mugthyjelvarwetmwyvj';

/**
 * O ref do LEGADO NÃO aparece neste arquivo. E a ausência foi aprendida.
 *
 * A primeira versão declarava `const PROJETO_LEGADO = 'whyhuza…'` para poder
 * recusá-lo com mensagem específica. Parecia melhor: negar por nome é mais
 * informativo do que negar por diferença.
 *
 * A auditoria do bundle mostrou o problema. O NOME da constante desaparece na
 * minificação; a STRING não. O ref do legado ficava no artefato publicado, sem
 * nenhum contexto em volta que dissesse "isto é uma recusa" — e qualquer
 * auditoria honesta do bundle tinha que acusá-lo.
 *
 * A conferência abaixo — "o ref é exatamente o do 2.0, ou recusa" — cobre o caso
 * do legado junto com todos os outros, e a mensagem nomeia o ref ENCONTRADO,
 * que veio da configuração e não de um literal nosso.
 *
 * Resultado: o console recusa o legado do mesmo jeito, e o identificador do
 * legado deixa de existir em qualquer coisa que a gente publique.
 */

export interface SupabaseConfig {
  readonly url: string;
  readonly publishableKey: string;
}

export class SupabaseConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SupabaseConfigError';
  }
}

function refDe(url: string): string | null {
  return /^https:\/\/([a-z0-9]+)\.supabase\.co\/?$/.exec(url.trim())?.[1] ?? null;
}

/**
 * Uma chave `service_role` tem forma reconhecível, e reconhecê-la é obrigação.
 *
 * O JWT legado carrega `"role":"service_role"` no payload, e a chave secreta
 * moderna começa com `sb_secret_`. Nenhuma das duas pode chegar ao browser —
 * elas ignoram RLS.
 *
 * A conferência não decodifica o JWT: procura a marca no texto. Um payload
 * base64 de `"role":"service_role"` também é reconhecível, e as duas formas
 * estão cobertas.
 */
export function pareceChaveSecreta(chave: string): boolean {
  const k = chave.trim();
  if (k.startsWith('sb_secret_')) return true;
  if (/service_role/.test(k)) return true;
  // O mesmo texto, em base64url, como aparece dentro de um JWT.
  if (/cm9sZSI6InNlcnZpY2Vfcm9sZ/.test(k)) return true;
  try {
    const payload = k.split('.')[1];
    if (payload !== undefined && payload !== '') {
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      if (/"role"\s*:\s*"service_role"/.test(json)) return true;
    }
  } catch {
    /* Não é JWT. As conferências acima já responderam o que dava para responder. */
  }
  return false;
}

/**
 * Lê e VALIDA a configuração. Recusa em vez de degradar.
 *
 * Um cliente construído com configuração errada não falha na hora: falha na
 * primeira consulta, com "não autorizado", e manda quem lê procurar problema de
 * RLS. Falhar aqui custa uma tela de erro e economiza uma investigação.
 */
export function readSupabaseConfig(env: Record<string, string | undefined>): SupabaseConfig {
  const url = (env.VITE_SUPABASE_URL ?? '').trim();
  const publishableKey = (env.VITE_SUPABASE_PUBLISHABLE_KEY ?? '').trim();

  if (url === '' || publishableKey === '') {
    throw new SupabaseConfigError(
      'VITE_SUPABASE_URL e VITE_SUPABASE_PUBLISHABLE_KEY são obrigatórias. ' +
        'Sem elas o console não tem a quem perguntar.',
    );
  }

  const ref = refDe(url);
  if (ref === null) {
    throw new SupabaseConfigError(
      `VITE_SUPABASE_URL não tem a forma https://<ref>.supabase.co — recebi "${url}".`,
    );
  }
  if (ref !== OPTEFISCAL_PROJECT_REF) {
    /*
     * Uma recusa só, para qualquer projeto que não seja o 2.0 — o legado
     * inclusive.
     *
     * O ref encontrado é impresso porque veio da CONFIGURAÇÃO, e não de um
     * literal escrito aqui. Quem lê a mensagem descobre para onde estava
     * apontando; o repositório não passa a carregar o identificador do legado.
     */
    throw new SupabaseConfigError(
      `VITE_SUPABASE_URL aponta para o projeto "${ref}", e o 2.0 é "${OPTEFISCAL_PROJECT_REF}" (§7). ` +
        'Se este for o projeto antigo: ele é evidência histórica, somente-leitura, e fora deste ' +
        'repositório. O console não lê nem escreve lá.',
    );
  }
  if (pareceChaveSecreta(publishableKey)) {
    throw new SupabaseConfigError(
      'VITE_SUPABASE_PUBLISHABLE_KEY tem forma de chave SECRETA. Chave secreta ignora RLS e ' +
        'nunca vai ao browser (§17). Use a chave publicável do projeto.',
    );
  }

  return { url, publishableKey };
}

let cliente: SupabaseClient | null = null;

/**
 * Um cliente por página.
 *
 * Dois clientes disputariam a mesma chave de sessão no `localStorage` e
 * produziriam renovações de token concorrentes — o tipo de bug que aparece uma
 * vez por semana e nunca no desenvolvimento.
 */
export function getSupabase(): SupabaseClient {
  if (cliente !== null) return cliente;

  const config = readSupabaseConfig(import.meta.env as unknown as Record<string, string>);
  cliente = createClient(config.url, config.publishableKey, {
    auth: {
      /*
       * Os três padrões documentados, escritos por extenso.
       *
       * Eles já são o default do SDK. Estão aqui porque sessão fiscal que
       * sobrevive a um refresh é comportamento que o §125 vai testar, e
       * comportamento testado não deve depender de um default que uma versão
       * futura da biblioteca pode mudar.
       */
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'optefiscal.auth',
    },
  });
  return cliente;
}

/** Só para teste: descarta o cliente memoizado. */
export function resetSupabaseForTest(): void {
  cliente = null;
}
